import ELK from 'elkjs';
import {nodeHeight, nodeWidth} from '../graph-rendering/constants';
import {GraphLayout, GraphNode, GraphEdge, QueryGraphLayoutAlgorithm} from '../interface';
import {ApplyLayoutWorkerInitData, ApplyLayoutWorkerResponseData} from '../../../workers/types';

interface Props {
    layoutAlgorithm: QueryGraphLayoutAlgorithm;
    nodes: GraphNode[];
    edges: GraphEdge[];
    onCalculated: (pos: string) => void;
}

interface Result {
    terminateCalculation: () => void;
}

export function calculateLayout({layoutAlgorithm, nodes, edges, onCalculated}: Props): Result {
    if (
        layoutAlgorithm === QueryGraphLayoutAlgorithm.LAYERED_HORIZONTAL ||
        layoutAlgorithm === QueryGraphLayoutAlgorithm.LAYERED_VERTICAL ||
        layoutAlgorithm === QueryGraphLayoutAlgorithm.MRTREE
    ) {
        const elk = new ELK({
            // providing workerFactory instead of just URL bc of err "Worker is not a constructor"
            // inside Elk source files when trying to calculate any layout
            workerFactory: () =>
                new Worker(
                    new URL(
                        /* webpackChunkName: "elk-worker" */ '../../../../src/workers/elk.worker',
                        import.meta.url
                    )
                )
        });

        elk.layout({
            id: 'root',
            layoutOptions: {
                'elk.algorithm':
                    layoutAlgorithm === QueryGraphLayoutAlgorithm.MRTREE ? 'mrtree' : 'layered',
                'elk.direction':
                    layoutAlgorithm === QueryGraphLayoutAlgorithm.LAYERED_HORIZONTAL
                        ? 'RIGHT'
                        : 'DOWN',
                'elk.spacing.nodeNode': '100'
            },
            children: nodes.map((node) => ({
                id: `${node.id}`,
                width: nodeWidth,
                height: nodeHeight
            })),
            edges: edges.map((edge) => ({
                id: `${edge.id}`,
                sources: [`${edge.startViewNodeId}`],
                targets: [`${edge.endViewNodeId}`]
            }))
        }).then((result) => {
            const layout: GraphLayout = {};
            result.children!.forEach((node) => {
                layout[node.id] = {x: node.x!, y: node.y!};
            });
            onCalculated(JSON.stringify(layout));
        });

        // @ts-ignore
        return {terminateCalculation: () => elk.worker.worker.terminate()};
    }

    const worker = new Worker(
        new URL(
            /* webpackChunkName: "apply-layout-worker" */ '../../../../src/workers/apply-layout.worker',
            import.meta.url
        )
    );
    worker.postMessage({nodes, edges, layoutAlgorithm} as ApplyLayoutWorkerInitData);

    worker.onmessage = (event: MessageEvent<ApplyLayoutWorkerResponseData>) => {
        onCalculated(JSON.stringify(event.data.layout));
        worker.terminate();
    };

    return {terminateCalculation: () => worker.terminate()};
}
